import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { parseIterationResponse } from '../../services/iterationResponse';

interface Props {
  strategyId?: string;
  onComplete: () => void;
}

const Module5Integration: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [integrationData, setIntegrationData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<{role: string, content: string}[]>([]);

  useEffect(() => {
    const loadExisting = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/strategies/${strategyId}`);
        const data = res.data;
        if (data && data.module_5_integration) {
          let parsed = data.module_5_integration;
          if (typeof parsed === 'string') {
            try { parsed = JSON.parse(parsed); } catch(e){}
          }
          if (parsed && (parsed.que || parsed.como)) {
            setIntegrationData(parsed);
          }
        }
      } catch (err) {
        console.error("Error cargando integración existente:", err);
      } finally {
        setLoading(false);
      }
    };
    if (strategyId) {
      loadExisting();
    }
  }, [strategyId]);

  const handleExportPDF = async () => {
    try {
      const res = await api.get(`/strategies/${strategyId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Estrategia_Modulo_V.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al exportar PDF: ' + err);
    }
  };

  const handleIterate = async () => {
    if (!chatInput) return;
    setLoading(true);
    try {
      const currentInput = chatInput;
      setChatInput('');
      const res = await api.post(`/strategies/${strategyId}/modules/5/chat`, { message: currentInput });
      const result = parseIterationResponse<any>(res.data);

      if (result.state && (result.state.que || result.state.como)) {
        setIntegrationData((prev: any) => ({ ...(prev || {}), ...result.state }));
      }

      const replyMsg = result.reply || 'He actualizado la estrategia según tus indicaciones.';
      setChatMessages(prev => [...prev, { role: 'user', content: currentInput }, { role: 'assistant', content: replyMsg }]);
    } catch (err) {
      alert('Error en iteración: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const generateIntegration = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/strategies/${strategyId}/modules/5/process`, {});
      let data = res.data;
      if (data.module_5_integration) {
        setIntegrationData(typeof data.module_5_integration === 'string' ? JSON.parse(data.module_5_integration) : data.module_5_integration);
      }
    } catch (err) {
      alert('Error: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      await api.put(`/strategies/${strategyId}/modules/5/update-state`, integrationData);
      onComplete();
    } catch (err) {
      alert('Error al guardar integración: ' + err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Módulo V: Estrategia de Integración</h2>
      <p style={styles.description}>El Agente INTEGRA toma todo lo anterior y lo formatea en un manifiesto o narrativa coherente.</p>

      {!integrationData ? (
        <button style={styles.primaryButton} onClick={generateIntegration} disabled={loading}>
          {loading ? 'Generando Narrativa Integradora...' : 'Generar Estrategia de Integración'}
        </button>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={styles.card}>
              <h3 style={{...styles.cardTitle, color: 'var(--accent)'}}>El QUÉ (El Cambio Deseado)</h3>
              <textarea
                style={styles.textarea}
                rows={4}
                value={integrationData.que || ''}
                onChange={(e) => setIntegrationData({...integrationData, que: e.target.value})}
              />
            </div>
            <div style={{...styles.card, background: 'var(--bg-card)'}}>
              <h3 style={{...styles.cardTitle, color: 'var(--success)'}}>El CÓMO (El Mecanismo)</h3>
              <textarea
                style={styles.textarea}
                rows={4}
                value={integrationData.como || ''}
                onChange={(e) => setIntegrationData({...integrationData, como: e.target.value})}
              />
            </div>

            <div style={{ marginTop: '24px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '16px', color: 'var(--text-primary)', marginBottom: '12px' }}>Iterar vía chat ("¿qué te hace ruido?")</h4>
              
              {(chatMessages && chatMessages.length > 0) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', maxHeight: '300px', overflowY: 'auto' }}>
                  {chatMessages.map((msg, i) => (
                    <div key={i} style={{ 
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      background: msg.role === 'user' ? 'var(--accent)' : 'var(--bg-card)',
                      color: msg.role === 'user' ? '#fff' : 'var(--text-primary)',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      maxWidth: '80%',
                      border: msg.role === 'assistant' ? '1px solid var(--border)' : 'none'
                    }}>
                      <p style={{ margin: 0, fontSize: '14px' }}>{msg.content}</p>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <input 
                  type="text" 
                  style={{ padding: '12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px', background: 'transparent', color: 'var(--text-primary)', flex: 1, fontFamily: 'inherit' }} 
                  value={chatInput} 
                  onChange={e => setChatInput(e.target.value)} 
                  placeholder="Escribe tus comentarios aquí..." 
                  onKeyDown={e => e.key === 'Enter' && handleIterate()}
                />
                <button style={styles.primaryButton} onClick={handleIterate} disabled={loading || !chatInput}>
                  {loading ? '...' : 'Enviar'}
                </button>
              </div>
            </div>

          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
            <button style={{ ...styles.secondaryButton, color: 'var(--accent)', borderColor: 'var(--accent)' }} onClick={handleExportPDF}>
              Exportar PDF
            </button>
            <button style={styles.successButton} onClick={handleApprove} disabled={loading}>
              {loading ? 'Guardando...' : 'Aprobar Narrativa'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '16px' },
  title: { fontSize: '24px', color: 'var(--text-primary)', marginBottom: '8px' },
  description: { color: 'var(--text-secondary)', marginBottom: '24px' },
  primaryButton: { background: 'var(--accent)', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' },
  secondaryButton: { background: 'transparent', color: 'var(--text-primary)', padding: '12px 24px', border: '1px solid var(--border)', borderRadius: '4px', fontWeight: 600, cursor: 'pointer' },
  successButton: { background: 'var(--success)', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-end', marginTop: '24px' },
  card: { padding: '24px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--bg-hover)' },
  cardTitle: { fontSize: '14px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '16px', letterSpacing: '0.05em' },
  textarea: { padding: '12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '16px', fontWeight: 600, fontFamily: 'inherit', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', resize: 'vertical' }
};

export default Module5Integration;
